import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  firmName: '',
  firmId: 0,
  adminsIdsNamesAndEmails: [],
  usersIdsNamesAndEmails: [],
  clientsIdsAndNames: [],
  knownFirms: [],
  unaffiliatedContacts: [],
  campaigns: [],
  facilities: [],
  intakers: [],
  clinicians: [],
  treatments: [],
  twilioCredentials: { id: 0, accountNumber: '', authToken: '', phoneNumber: '' },
  dropdowns: [],
};

const extractLastName = (name) => {
  const lastSpaceLoc = name.lastIndexOf(' ');
  return name.slice(lastSpaceLoc + 1);
};

export const firmInfoSlice = createSlice({
  name: 'firmInfo',
  initialState: initialState,
  reducers: {
    clearFirmInfo: () => {
      return initialState;
    },

    setFirmInfo: (state, action) => {
      return action.payload;
    },

    addUserToFirm: (state, action) => {
      const tripleToAdd = action.payload;
      let { usersIdsNamesAndEmails } = state;
      if (usersIdsNamesAndEmails.some((triple) => triple.id === tripleToAdd.id)) {
        return state;
      } else {
        usersIdsNamesAndEmails = usersIdsNamesAndEmails.concat(tripleToAdd);
        return { ...state, usersIdsNamesAndEmails };
      }
    },

    addAdminToFirm: (state, action) => {
      const tripleToAdd = action.payload;
      let { adminsIdsNamesAndEmails } = state;
      if (adminsIdsNamesAndEmails.some((triple) => triple.id === tripleToAdd.id)) {
        return state;
      } else {
        adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.concat(tripleToAdd);
        return { ...state, adminsIdsNamesAndEmails };
      }
    },

    removeUserFromFirm: (state, action) => {
      const idToRemove = action.payload;
      let { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = state;
      adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.filter((triple) => triple.id !== idToRemove);
      usersIdsNamesAndEmails = usersIdsNamesAndEmails.filter((triple) => triple.id !== idToRemove);
      return { ...state, adminsIdsNamesAndEmails, usersIdsNamesAndEmails };
    },

    promoteUser: (state, action) => {
      const idToPromote = action.payload;
      let { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = state;
      const matchingPair = usersIdsNamesAndEmails.find((triple) => triple.id === idToPromote);
      usersIdsNamesAndEmails = usersIdsNamesAndEmails.filter((triple) => triple.id !== idToPromote);
      adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.concat(matchingPair);
      usersIdsNamesAndEmails = usersIdsNamesAndEmails.sort((t1, t2) => t1.id.localeCompare(t2.id));
      adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.sort((t1, t2) => t1.id.localeCompare(t2.id));
      return { ...state, adminsIdsNamesAndEmails, usersIdsNamesAndEmails };
    },

    demoteUser: (state, action) => {
      const idToDemote = action.payload;
      let { adminsIdsNamesAndEmails, usersIdsNamesAndEmails } = state;
      const matchingPair = adminsIdsNamesAndEmails.find((triple) => triple.id === idToDemote);
      adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.filter((triple) => triple.id !== idToDemote);
      usersIdsNamesAndEmails = usersIdsNamesAndEmails.concat(matchingPair);
      usersIdsNamesAndEmails = usersIdsNamesAndEmails.sort((t1, t2) => t1.id.localeCompare(t2.id));
      adminsIdsNamesAndEmails = adminsIdsNamesAndEmails.sort((t1, t2) => t1.id.localeCompare(t2.id));
      return { ...state, adminsIdsNamesAndEmails, usersIdsNamesAndEmails };
    },

    addVisibleFirm: (state, action) => {
      const { firmType, firmId, name, contacts } = action.payload;
      const newFirm = { id: firmId, name, firmType, contacts };
      const newUnsortedFirmList = state.knownFirms.concat(newFirm);
      const newFirmList = newUnsortedFirmList.sort((f1, f2) => f1.name.localeCompare(f2.name));
      return { ...state, knownFirms: newFirmList };
    },

    updateVisibleFirm: (state, action) => {
      const { firmType, firmId, name, contacts } = action.payload;
      const newFirm = { id: firmId, name, firmType, contacts };
      const filteredList = state.knownFirms.filter((f) => f.id !== firmId);
      const newUnsortedFirmList = filteredList.concat(newFirm);
      const newFirmList = newUnsortedFirmList.sort((f1, f2) => f1.name.localeCompare(f2.name));
      return { ...state, knownFirms: newFirmList };
    },

    addFirmContact: (state, action) => {
      const contactFirmId = action.payload.referralFirmId;
      const personIdAndName = action.payload.personIdAndName;

      const oldFirm = state.knownFirms.find((f) => f.id === contactFirmId);
      const prunedFirmList = state.knownFirms.filter((f) => f.id !== contactFirmId);
      const newUnsortedContactList = oldFirm.contacts.concat(personIdAndName);
      const sortedContactList = newUnsortedContactList.sort((c1, c2) =>
        extractLastName(c1.name).localeCompare(extractLastName(c2.name))
      );
      const newFirmWithExtendedContacts = { ...oldFirm, contacts: sortedContactList };
      const newUnsortedFirmList = prunedFirmList.concat(newFirmWithExtendedContacts);
      const newFirmList = newUnsortedFirmList.sort((f1, f2) => f1.name.localeCompare(f2.name));

      return { ...state, knownFirms: newFirmList };
    },

    addUnaffiliatedContact: (state, action) => {
      const contact = action.payload;
      const newList = [...state.unaffiliatedContacts, contact];
      return { ...state, unaffiliatedContacts: newList };
    },

    removeUnaffiliatedContact: (state, action) => {
      const contactIdToRemove = action.payload;
      const newList = state.unaffiliatedContacts.filter((ctct) => ctct.id != contactIdToRemove);
      return { ...state, unaffiliatedContacts: newList };
    },

    addFirmCampaign: (state, action) => {
      const campaign = action.payload;
      const newCampaignList = state.campaigns.concat(campaign);
      return { ...state, campaigns: newCampaignList };
    },

    modifyFirmCampaign: (state, action) => {
      const campaign = action.payload;
      const otherCampaigns = state.campaigns.filter((c) => c.campaignId !== campaign.campaignId);
      const newCampaignList = otherCampaigns.concat(campaign);
      return { ...state, campaigns: newCampaignList };
    },

    setFirmDropdowns: (state, action) => {
      return { ...state, dropdowns: action.payload };
    },

    updateFirmDropdown: (state, action) => {
      const dropdown = action.payload;
      const others = state.dropdowns.filter((dd) => dd.dropdownId !== dropdown.dropdownId);
      const amendedDropdowns = others.concat(dropdown);
      return { ...state, dropdowns: amendedDropdowns };
    },
  },
});

export const {
  clearFirmInfo,
  setFirmInfo,
  addUserToFirm,
  addAdminToFirm,
  removeUserFromFirm,
  promoteUser,
  demoteUser,
  addVisibleFirm,
  updateVisibleFirm,
  addFirmContact,
  addUnaffiliatedContact,
  removeUnaffiliatedContact,
  addFirmCampaign,
  modifyFirmCampaign,
  setFirmDropdowns,
  updateFirmDropdown,
} = firmInfoSlice.actions;

export default firmInfoSlice.reducer;
